import React from 'react';

import {UserAutosuggest} from 'src/components/subscription/suggest/user-autosuggest';
import {WatcherView} from 'src/components/subscription/watcher/watcher.view';
import {ChangeRequestSubscription} from 'src/openapi/models/ChangeRequestSubscription';
import {EventSubscription} from 'src/openapi/models/EventSubscription';
import {User} from 'src/openapi/models/User';

import {FlatButton} from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {DropdownMenu, DropdownMenuItem} from '@pb/kit-component-dropdown-menu';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons, Sizes} from '@pb/kit-component-icon';
import {Scrollable} from '@pb/kit-component-scrollable';

import {subscription} from './subscription.module.styl';

type Props = {
    title: string,
    disabled: boolean,
    subscribed: boolean,
    shownAutoSuggest: boolean,
    shownSubscription: boolean,
    userEmail: string,
    watchers: Array<ChangeRequestSubscription> | Array<EventSubscription>,
    watchersEmails: string[],
    setShownAutoSuggest: (show: boolean) => void,
    setShown: (show: boolean) => void,
    setSubscribed: (subscribed: boolean) => void,
    onWatcherRemove: (changeRequestId: number) => void,
    saveSubscription: (email: string) => void,
};

export const Subscription = (props: Props) => {
    const {
        title,
        disabled,
        subscribed,
        shownAutoSuggest,
        shownSubscription,
        userEmail,
        watchers,
        watchersEmails,
        setShownAutoSuggest,
        setShown,
        setSubscribed,
        onWatcherRemove,
        saveSubscription,
    } = props;

    const removeUserSubscription = () => {
        const watcher = watchers.find(item => item.userEmail === userEmail);
        if (watcher?.id) {
            onWatcherRemove(watcher.id);
            setSubscribed(false);
        }
    };

    const onUserAutosuggestSelect = (user: User) => {
        saveSubscription(user.email);
        setShownAutoSuggest(false);
    };

    const onAddWatchers = () => {
        setShownAutoSuggest(true);
    };

    const onSubscribe = () => {
        if (subscribed) {
            setShownAutoSuggest(false);
            removeUserSubscription();
            return;
        }

        setShownAutoSuggest(false);
        saveSubscription(userEmail);
    };

    const onSubscriptionClick = () => {
        if (shownSubscription) {
            return;
        }

        if (shownAutoSuggest) {
            setShownAutoSuggest(false);
        }
    };

    const onClose = () => {
        setShown(false);
    };

    return (
        <Group gap="xxxl">
            <DropdownMenu
                hideOnItemClick={false}
                shown={shownSubscription}
                setShown={setShown}
                hideOnBodyLeave={false}
                anchor={({getAnchorProps, anchorRef}) => (
                    <Group {...getAnchorProps()}>
                        <FlatButton
                            variant="secondary"
                            ref={anchorRef}
                            startIcon={(<Icon type={subscribed ? Icons.BELL : Icons.BELL_LIGHT} size={Sizes.Size_19}/>)}
                            disabled={disabled}
                            onClick={onSubscriptionClick}>
                            {`(${watchers.length})`}
                        </FlatButton>
                    </Group>
                )}>
                <Item style={{width: '20rem'}}>
                    <DialogHeader onClose={onClose}>
                        <DropdownMenuItem
                            caption={subscribed ? 'Stop watching' : 'Start watching'}
                            iconBefore={subscribed ? Icons.BELL_LIGHT : Icons.BELL}
                            onClick={onSubscribe}
                        />
                    </DialogHeader>
                    <DialogMain className="pdy-s">
                        <Group justify="center" className="pd-s">
                            <strong>{title}</strong>
                        </Group>
                        <Item className={subscription}>
                            <Scrollable>
                                {watchers ? watchers.map(watcher => (
                                    <WatcherView onRemove={onWatcherRemove} watcher={watcher} key={watcher.id}/>
                                )) : null}
                            </Scrollable>
                        </Item>
                    </DialogMain>
                    <DialogFooter>
                        {shownAutoSuggest ? (
                            <Item style={{width: '100%'}}>
                                <UserAutosuggest
                                    onSelect={onUserAutosuggestSelect}
                                    watchersEmails={watchersEmails}
                                />
                            </Item>
                        ) : (
                            <DropdownMenuItem
                                style={{width: '100%'}}
                                caption="Add watchers"
                                iconBefore={Icons.PLUS}
                                onClick={onAddWatchers}
                            />
                        )}
                    </DialogFooter>
                </Item>
            </DropdownMenu>
        </Group>
    );
};
